import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Flame, Music, Palette, Calendar, ChevronRight, ChefHat } from "lucide-react";

const EVENTS = [
  {
    id: "brasero",
    icon: Flame,
    color: "#C1624D",
    image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600&h=400&fit=crop&q=80",
    imageAlt: "Cuisine au brasero en terrasse",
  },
  {
    id: "theme",
    icon: Palette,
    color: "#6F7D4E",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop&q=80",
    imageAlt: "Soirée à thème au restaurant",
  },
  {
    id: "concert",
    icon: Music,
    color: "#A5C4D4",
    image: "https://images.unsplash.com/photo-1501612780327-45045538702b?w=600&h=400&fit=crop&q=80",
    imageAlt: "Concert live au restaurant",
  },
  {
    id: "privatisation",
    icon: ChefHat,
    color: "#8B6F4E",
    image: "https://images.unsplash.com/photo-1530062845289-9109b2c9c868?w=600&h=400&fit=crop&q=80",
    imageAlt: "Salle privatisée pour événement",
  },
];

const FEATURED = [];

const upcomingAgenda = () => {
  const now = new Date();
  return FEATURED.filter((f) => f.recurring || new Date(`${f.date}T23:59:59`) >= now).sort((a, b) => {
    if (a.recurring) return -1;
    if (b.recurring) return 1;
    return new Date(a.date) - new Date(b.date);
  });
};

const EventCard = ({ event, index, isActive, onClick }) => {
  const { t } = useTranslation();
  const Icon = event.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={onClick}
      className={`cursor-pointer p-6 rounded-sm border transition-all duration-400 ${
        isActive
          ? "bg-white border-[#D8D0C5] shadow-md"
          : "bg-transparent border-transparent hover:bg-white/50 hover:border-[#D8D0C5]/50"
      }`}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300"
          style={{ backgroundColor: `${event.color}15` }}
        >
          <Icon size={22} style={{ color: event.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className="text-xl font-normal text-[#2C2420] mb-1"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t(`events.${event.id}_title`)}
          </h4>
          <p
            className="text-sm text-[#8C847F]"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t(`events.${event.id}_subtitle`)}
          </p>
        </div>
        <ChevronRight
          size={18}
          className={`flex-shrink-0 transition-transform duration-300 text-[#8C847F] ${
            isActive ? "rotate-90" : ""
          }`}
        />
      </div>
    </motion.div>
  );
};

export const EventsSection = () => {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeEvent, setActiveEvent] = useState("brasero");

  const currentEvent = EVENTS.find((e) => e.id === activeEvent);

  return (
    <section
      id="evenements"
      data-testid="events-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#C1624D] mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("events.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="events-heading"
          >
            {t("events.heading")}
          </h2>
          <p
            className="text-base text-[#8C847F] max-w-xl"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("events.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left: Event list */}
          <div className="lg:col-span-2 space-y-2">
            {EVENTS.map((event, i) => (
              <EventCard
                key={event.id}
                event={event}
                index={i}
                isActive={activeEvent === event.id}
                onClick={() => setActiveEvent(event.id)}
              />
            ))}

            {/* Agenda */}
            {upcomingAgenda().length > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-8 text-xs uppercase tracking-[0.25em] text-[#8C847F]"
              style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700 }}
              data-testid="agenda-title"
            >
              {t("events.agenda_title")}
            </motion.p>
            )}
            {upcomingAgenda().map((f, i) => {
              const FIcon = f.icon;
              return (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                  onClick={() => setActiveEvent(f.event)}
                  className="mt-4 rounded-sm bg-[#2C2420] border cursor-pointer transition-colors duration-300 flex overflow-hidden"
                  style={{ borderColor: `${f.color}66` }}
                  data-testid={`next-${f.id}-card`}
                >
                  {/* Date badge */}
                  <div
                    className="flex flex-col items-center justify-center w-20 flex-shrink-0 py-5"
                    style={{ backgroundColor: `${f.color}22` }}
                  >
                    {f.recurring ? (
                      <FIcon size={26} style={{ color: f.color }} />
                    ) : (
                      <>
                        <span
                          className="text-2xl text-white leading-none"
                          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                        >
                          {new Date(f.date).getDate()}
                        </span>
                        <span
                          className="text-[10px] uppercase tracking-[0.15em] mt-1"
                          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, color: f.color }}
                        >
                          {new Intl.DateTimeFormat(i18n.language, { month: "short" }).format(new Date(f.date))}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-5 flex-1">
                    <p
                      className="text-xs uppercase tracking-[0.25em] mb-1"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, color: f.color }}
                    >
                      {t(`events.next_${f.id}_label`)}
                    </p>
                    <p
                      className="text-xl italic text-white mb-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {t(`events.next_${f.id}_name`)}
                    </p>
                    <p
                      className="text-sm text-white/70 mb-3"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                    >
                      {t(`events.next_${f.id}_date`)}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = document.querySelector("#reservation");
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="px-5 py-2 rounded-full bg-[#C1624D] text-white font-serif italic text-base
                                 hover:bg-[#A6503C] active:scale-95 transition-all duration-300 shadow-md"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      data-testid={`next-${f.id}-reserve-btn`}
                    >
                      {t(`events.next_${f.id}_cta`)}
                    </button>
                  </div>
                </motion.div>
              );
            })}

            {/* Schedule badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-6 p-4 rounded-sm bg-[#F5E6D3]/60 border border-[#D8D0C5]/50"
            >
              <div className="flex items-center gap-3">
                <Calendar size={16} className="text-[#C1624D]" />
                <p
                  className="text-sm text-[#5D544F]"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  {t("events.follow_us")}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right: Event detail */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {currentEvent && (
                <motion.div
                  key={currentEvent.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`event-detail-${currentEvent.id}`}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden rounded-sm mb-8">
                    <img
                      src={currentEvent.image}
                      alt={currentEvent.imageAlt}
                      className="w-full h-[280px] md:h-[340px] object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <span
                        className="px-4 py-1.5 rounded-full text-sm text-white"
                        style={{
                          backgroundColor: currentEvent.color,
                          fontFamily: "'Lato', sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        {t(`events.${currentEvent.id}_subtitle`)}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3
                    className="text-2xl md:text-3xl font-light text-[#2C2420] mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t(`events.${currentEvent.id}_title`)}
                  </h3>
                  <p
                    className="text-base text-[#5D544F] leading-relaxed mb-6"
                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                  >
                    {t(`events.${currentEvent.id}_description`)}
                  </p>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-3">
                    {[1, 2, 3, 4].map((n) => (
                      <div
                        key={n}
                        className="flex items-center gap-2 text-sm text-[#5D544F]"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: currentEvent.color }}
                        />
                        {t(`events.${currentEvent.id}_detail_${n}`)}
                      </div>
                    ))}
                  </div>

                  {/* Brasero pause notice */}
                  {currentEvent.id === "brasero" && (
                    <div
                      className="mt-8 p-5 rounded-sm bg-[#C1624D]/10 border border-[#C1624D]/30 flex items-start gap-3"
                      data-testid="brasero-pause-notice"
                    >
                      <Flame size={18} className="text-[#C1624D] flex-shrink-0 mt-0.5" />
                      <p
                        className="text-sm text-[#5D544F] leading-relaxed"
                        style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
                      >
                        {t("events.brasero_pause")}
                      </p>
                    </div>
                  )}

                  {/* Brasero set menus */}
                  {currentEvent.id === "brasero" && (
                    <div
                      className="mt-8 p-6 rounded-sm bg-[#F5E6D3]/60 border border-[#D8D0C5]"
                      data-testid="brasero-formulas"
                    >
                      <h4
                        className="text-xl font-normal text-[#2C2420] mb-4"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {t("events.brasero_formulas_title")}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
                        {[1, 2, 3, 4].map((n) => (
                          <div
                            key={n}
                            className="flex items-center gap-2 text-sm text-[#5D544F]"
                            style={{ fontFamily: "'Lato', sans-serif" }}
                            data-testid={`brasero-formula-${n}`}
                          >
                            <Flame size={14} className="text-[#C1624D] flex-shrink-0" />
                            {t(`events.brasero_formula_${n}`)}
                          </div>
                        ))}
                      </div>
                      <p
                        className="text-xs italic text-[#8C847F]"
                        style={{ fontFamily: "'Lato', sans-serif" }}
                      >
                        {t("events.brasero_formula_note")}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
